/* eslint-disable max-statements */
import {
  Component,
  Input,
  Output,
  ElementRef,
  AfterViewInit,
  DoCheck,
  EventEmitter,
  ChangeDetectionStrategy,
  OnChanges,
  OnDestroy,
} from '@angular/core';

import Konva from 'konva';
import {
  setPhoto,
  setCrossLines,
  setStretching,
  setRepeat,
  setScale,
  setPosition,
  setOffset,
  loadKonvaImage

} from './helpers';
import {
  STROKE_WIDTH,
  MIN_STROKE_WIDTH,
  MAX_STROKE_WIDTH,
  ZOOM,
  TEXT_COLOR,
  FONT_SIZE,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE,
  DEFAULT_FILL
} from './base';

import { Options } from './sketch.interfaces';
import { Image } from 'canvas';
import { ComplexConfiguratorField } from '@icc/shared/types/complex/ComplexConfiguratorField';


@Component({
  selector: 'icc-sketch',
  templateUrl: './sketch.component.html',
  styleUrls: ['./sketch.component.scss'],
  exportAs: 'iccSketch',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class SketchComponent implements OnChanges, AfterViewInit, OnDestroy {

  constructor(private el:ElementRef) { }

  private _sketchData: any = null;
  private _sketchPhotos: any = null;
  private _optionPhotos: any = null;
  private _crossLines: any = null;
  private _details: any = null;
  private _options: any = null;
  private _platform: 'browser' | 'server' = 'browser';
  private _host: string|null = null;
  private sketchDataIsLoaded = false;

  @Input()
  get sketchData():any{
    return this._sketchData;
  }
  set sketchData(sketchData:any){
    this._sketchData = sketchData && JSON.parse(JSON.stringify(sketchData));
  }

  @Input()
  get sketchPhotos():any{
    return this._sketchPhotos;
  }
  set sketchPhotos(sketchPhotos:any){
    this._sketchPhotos = sketchPhotos ? sketchPhotos : null;
  }

  @Input()
  get optionPhotos():any{
    return this._optionPhotos;
  }
  set optionPhotos(optionPhotos:any){
    this._optionPhotos = optionPhotos ? optionPhotos : null;
  }

  @Input()
  get details():any{
    return this._details;
  }
  set details(details:any){
    this._details = details;
  }

  @Input()
  get options():any{
    return this._options;
  }
  set options(options:any){
    this._options = options;
  }

  @Input()
  get host():string|null{
    return this._host;
  }
  set host(host:string|null){
    this._host = host;
  }

  @Input()previewWidth = null;
  @Input()previewHeight = null;

  @Input()width: number | null = null;
  @Input()height: number | null = null;

  @Input()redrawCycle = true;

  @Input()getSketchData = false;

  @Input()konvaContainerName = 'konvaContainer-'+ Date.now() + '-' + Math.floor(Math.random() * 10000);

  @Output() sketchDataEmitter = new EventEmitter();

  konva: Konva.Stage | null = null;
  private konvaWidth = 120;
  private konvaHeight = 120;
  private mainLayer: Konva.Layer | null = null;
  private drawingLayer: Konva.Group | null = null;
  private zoomScale = ZOOM;
  private strokeWidth = STROKE_WIDTH;
  private entitiesTreeList:any[] = [];
  private loadedPhotos : Record<string,Image> = {};
  private loadedOptionPhotos: Record<string,Image> = {};
  private loadedCrossLines: Record<string,Image> = {};
  private _timestamp = 0;
  private viewport = {
    percentages : {
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,
    },
    millimeters : {
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,
    },
    pixels : {
      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,
    },
    entity:<any|null>null
  };

  private marginTopPx = 0;
  private marginRightPx = 0;
  private marginBottomPx = 0;
  private marginLeftPx = 0;



  setOptions(options:Options){
    this.sketchData = options.sketchData;
    if(options?.sketchPhotos)this.sketchPhotos = options.sketchPhotos;
    if(options.optionPhotos)this._optionPhotos = options.optionPhotos;
    if(options.crossLines)this._crossLines = options.crossLines;
    if(options?.previewWidth)this.previewWidth = options.previewWidth;
    if(options?.previewHeight)this.previewHeight = options.previewHeight;
    if(options?.width)this.width = options.width;
    if(options?.height)this.height = options.height;
    if(options.platform)this._platform = options.platform;
    if(options.details)this._details = options.details;
    if(options.host)this._host = options.host;
    if(options.redrawCycle)this.redrawCycle = options.redrawCycle;
    if(this.width && this.height){
      this.previewWidth = this.width;
      this.previewHeight = this.height;
    }
  }

  ngAfterViewInit(): void {

    if(this._sketchData && !this.konva){
      if(!this._host && window?.location?.protocol && window?.location?.hostname){
        this._host = window.location.protocol + "//" + window.location.hostname
      }
      this.init();
    }

  }

  async init(){

    if(this.width && this.height){
      this.previewWidth = this.width;
      this.previewHeight = this.height;
    }

    this.initializeKonva();
    this._platform === 'server'
      ? await this.handleSketchData()
      : this.handleSketchData();
    this.handleObjectsXY();
    this.zoom(this.zoomScale);
    this.handleViewPort();

  }

  ngOnChanges(): void {
    this.update();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  destroy(): void {
    this.konva?.destroy();
    this.konva = null;
    this.mainLayer = null;
    this.drawingLayer = null;
    this.entitiesTreeList = [];
    this.loadedPhotos = {};
    this.loadedOptionPhotos = {};
    this.loadedCrossLines = {};
  }

  async update(){
    if(this.konva && this._details && this.sketchData){
      this._timestamp = this._details.timestamp;
      this.sketchDataIsLoaded = true;
      this.setMarginsPx();
      this.handleObjectsXY(true);
      this.handleDimensioning();
      this.handleParameters();
      this._platform === 'server'
        ? await this.handleGraphical()
        : this.handleGraphical();
      this.handleViewPort();
      this.handleEmitSketchData();
    }
  }

  initializeKonva() {

    const options: Konva.StageConfig = {
        container: null,
        width: this.konvaWidth,
        height: this.konvaHeight,
        draggable: false,
    };

    if(this._platform === 'browser'){
      options.container = this.konvaContainerName;
    }

    this.konva = new Konva.Stage(options);

    this.mainLayer = new Konva.Layer();
    this.konva.add(this.mainLayer);


    this.drawingLayer = new Konva.Group({
        draggable: false,
    });

    this.mainLayer.add(this.drawingLayer);
    this.mainLayer.draw();

  }

  async handleSketchData(){
    if(this._sketchData && this.konva){

      if(typeof this.sketchData?.entities !== 'undefined'){
        this.sketchData.entities.reverse();
        this.handleEntitiesFromSketchData(this.sketchData.entities);
        await this.handleGraphicalParameters(this.sketchData.entities);
        this.handleEntitiesReference(this.sketchData.entities);
        this.konva.draw();
      }
      if(typeof this._sketchData.workspace !== 'undefined' ){
        this.handleWorkspaceData();
      }

    }
  }

  handleViewPortInWorkspaceData(viewport:any){
    if(viewport.entity){
      const entity = viewport?.entity?.id ? viewport.entity : this.findEntityById(+ viewport.entity);
      if(entity){
        this.viewport = viewport;
        this.viewport.entity = entity;

        if(!this.viewport.pixels){
          this.viewport.pixels = {
            marginTop: 0,
            marginRight: 0,
            marginBottom: 0,
            marginLeft: 0,
          }
        }
      }
    }
  }

  checkAndPrepareViewPortData(){
    if(
      !this.sketchData?.workspace?.viewport?.percentages
      && this.sketchData?.workspace?.viewport?.marginTop
      && this.sketchData?.workspace?.viewport?.marginRight
      && this.sketchData?.workspace?.viewport?.marginBottom
      && this.sketchData?.workspace?.viewport?.marginLeft
    ){

      const type = this.sketchData?.workspace?.viewport?.marginType?.type
        ? this.sketchData.workspace.viewport.marginType.type
        : 'percentages';

      this.sketchData.workspace.viewport = {
        percentages : {
          marginTop: type === 'percentages' ? this.sketchData?.workspace.viewport.marginTop : 0,
          marginRight: type === 'percentages' ? this.sketchData?.workspace.viewport.marginRight : 0,
          marginBottom: type === 'percentages' ? this.sketchData?.workspace.viewport.marginBottom : 0,
          marginLeft: type === 'percentages' ? this.sketchData?.workspace.viewport.marginLeft : 0,
        },
        millimeters : {
          marginTop: type === 'millimeters' ? this.sketchData.workspace.viewport.marginTop : 0,
          marginRight: type === 'millimeters' ? this.sketchData.workspace.viewport.marginRight : 0,
          marginBottom: type === 'millimeters' ? this.sketchData.workspace.viewport.marginBottom : 0,
          marginLeft: type === 'millimeters' ? this.sketchData.workspace.viewport.marginLeft : 0,
        },
        pixels : {
          marginTop: 0,
          marginRight: 0,
          marginBottom: 0,
          marginLeft: 0,
        }
      };

    }
  }

  handleWorkspaceData(){

    let setSize = true;
    if(typeof this.sketchData.workspace.viewport !== 'undefined'){
      this.checkAndPrepareViewPortData();
      this.handleViewPortInWorkspaceData(this.sketchData.workspace.viewport);
      if(this.viewport.entity){
        setSize = false;
        this.handleViewPort();
      }
    }

    if(setSize){
      if(typeof this.sketchData.workspace.width !== 'undefined'){
        this.setKonvaWidth(this.sketchData.workspace.width);
      }
      if(typeof this.sketchData.workspace.height !== 'undefined'){
        this.setKonvaHeight(this.sketchData.workspace.height);
      }
      if(typeof this.sketchData.workspace.zoomScale !== 'undefined'){
        this.zoom(this.sketchData.workspace.zoomScale);
      }
    }
  }

  handleDimensioning(){

    if(this._details && this.entitiesTreeList){
      for (const entity of this.entitiesTreeList) {
        if(entity.konvaObject){
          if(entity?.xParameters?.dimensioning?.feature?.fieldId){
            const width = this.getFeatureValue(entity.xParameters.dimensioning.feature.fieldId);
            if(width){
              this.setEntityWidth(entity,['circle'].includes(entity.type) ? (+width)*2 : +width);
            }
          }
          if(entity?.yParameters?.dimensioning?.feature?.fieldId){
            const height = this.getFeatureValue(entity.yParameters.dimensioning.feature.fieldId);
            if(height && !['circle'].includes(entity.type)){
              this.setEntityHeight(entity,+height);
            }
          }
          this.setEntityXDimension(entity);
          this.setEntityYDimension(entity);
          this.handleStretching(entity);

        }
      }
    }
  }

  handleStretching(entity:any){
    if(
      ![null,0].includes(entity.graphicalParameters.percentWidth)
      || ![null,0].includes(entity.graphicalParameters.percentHeight)
    ){
      if(![null,0].includes(entity.graphicalParameters.percentWidth)){
        setScale(entity,'width');
      }
        if(![null,0].includes(entity.graphicalParameters.percentHeight)){
        setScale(entity,'height');
      }
    }else{
      setStretching(entity);
    }
  }

  async handleGraphical(){
    if(this._details && this.entitiesTreeList){

      for (const entity of this.entitiesTreeList) {
        if(entity.konvaObject){
          if(entity?.graphicalParameters?.fieldId){
            const fieldId = entity.graphicalParameters.fieldId;
            const optionId = this.getFeatureValue(String(fieldId));
            if(optionId && optionId !== entity.graphicalParameters.optionId){
              const option = this.getFeatureOption(String(fieldId), + optionId);
              this._platform === 'server'
                ? await this.handleGraphicalFill(entity,option?.value, option?.img)
                : this.handleGraphicalFill(entity,option?.value, option?.img);
              entity.graphicalParameters.optionId = optionId;
            }
          }
        }
      }
    }
  }

  async handleGraphicalFill(entity:any,value:string | undefined, img:string| undefined){
    if(value && this.isHex(value)){
      entity.graphicalParameters.optionColor = value;
      entity.konvaObject.fill(value);
    }else if (img){
      this._platform === 'server'
        ? await this.setOptionPhoto(entity,img)
        : this.setOptionPhoto(entity,img);

      this.setEntityGraphicalParameters(entity);
    }else{
      entity.konvaObject.fill(DEFAULT_FILL);
    }

  }

  async setOptionPhoto(entity:any,optionPhoto:string){
    if(optionPhoto){
      entity.konvaObject.fill(null);
      const image = await this.getOptionPhotoImg(optionPhoto);
      if(image && image?.width && image?.height){
        const [widthMM,heightMM] = this.getMMSize(optionPhoto,this.getOptionPhotos());
        entity.graphicalParameters.optionPhoto = {
          img:optionPhoto,
          width : image.width,
          height : image.height,
          width_mm : + widthMM,
          height_mm : + heightMM,
        }
        entity.konvaObject.fillPatternImage(image);
        this.handleStretching(entity);
      }
    }
  }

  getMMSize(imgName:string ,images:any){
    for (const imageKey in images) {
      if(imageKey){
        const image = images[imageKey];
        if(
            (imgName && image.img === imgName)
        ){
          return [image.width_mm,image.height_mm];
        }
      }
    }
    return [null,null];
  }

  async getOptionPhotoImg(photo:string) {
    const photoSrc = photo ? '/files/productfeatureoptions/' + photo : '';
    if (this.loadedPhotos[photoSrc]) {
        return this.loadedPhotos[photoSrc];
    }
    let image = null;
    try {
      image = await loadKonvaImage(photoSrc,this._host);
    } catch (error) {
      console.log(error);
    }
    this.loadedPhotos[photoSrc] = image;
    return image;
  }

  handleParameters(){

    if(this._details && this.entitiesTreeList){
      for (const entity of this.entitiesTreeList) {
        if(entity.type === 'variable' && entity?.parameters?.fieldId){
          const value = this.getFeatureValue(entity?.parameters?.fieldId);
          if(value){
            entity.konvaObject.text(value + 'mm');
          }
        }
      }

    }

  }

  handleEntityMirror(entity:any){
    if(entity.mirror.x || entity.mirror.y){
      this.handleStretching(entity);
    }
  }

  handleEntitiesFromSketchData(entitiesFromSketchData: any){
    if(this.drawingLayer){
      for (const entity of entitiesFromSketchData) {
        entity.konvaObject = this.handleObjectDataFromSketchData(entity);
        if(entity.objectData.rotation){
          entity.konvaObject.rotation(entity.objectData.rotation);
        }
        if(entity?.objectData?.zIndex){
          entity.konvaObject.zIndex(entity.objectData.zIndex);
        }
        this.handleEntityMirror(entity);
        entity.entities = [];
        this.drawingLayer.add(entity.konvaObject);
        this.addEntityToTreeList(entity);
      }
    }
  }

  handleObjectX(entity:any, handlAllEntities = false){
    if(
      this.viewport?.entity?.konvaObject
      && entity?.konvaObject
      && (!entity.xParameters.reference.referenceEntity || handlAllEntities)
    ){
      const marginLeft =
        (Math.round(
          (this.getEntityWidth(this.viewport.entity) / 100)*  Number(this.viewport.percentages.marginLeft))
          +  Number(this.viewport.millimeters.marginLeft)
          +  this.marginLeftPx
        );

      if(this.viewport.entity === entity){
        this.setEntityX(entity,marginLeft);
      }else{
        const differenceX = entity.objectData.x - this.viewport.entity.objectData.x;
        this.setEntityX(entity,marginLeft + differenceX);
      }
    }
  }

  handleObjectY(entity:any, handlAllEntities = false){
    if(
      this.viewport?.entity?.konvaObject
      && entity?.konvaObject
      && (!entity.yParameters.reference.referenceEntity || handlAllEntities)
    ){
      const marginTop =
        Math.round(
          ((this.getEntityHeight(this.viewport.entity) / 100)*  Number(this.viewport.percentages.marginTop))
          +  Number(this.viewport.millimeters.marginTop)
          +  this.marginTopPx
        );

      if(this.viewport.entity === entity){
        this.setEntityY(entity,marginTop);
      }else{
        const differenceY = entity.objectData.y - this.viewport.entity.objectData.y;
        this.setEntityY(entity,marginTop + differenceY);
      }
    }
  }

  handleObjectsXY(handlAllEntities = false){

    if(this.entitiesTreeList && this.viewport?.entity) {
      for (const entity of this.entitiesTreeList) {
        this.handleObjectX(entity,handlAllEntities);
        this.handleObjectY(entity,handlAllEntities);
      }
    }

  }

  handleObjectDataFromSketchData(entityFromSketchData: any){
    switch (entityFromSketchData.type) {
      case 'rectangle':
        return this.newRectangle(entityFromSketchData.objectData);
      case 'circle':
        entityFromSketchData.objectData.x += entityFromSketchData.objectData.width / 2;
        entityFromSketchData.objectData.y += entityFromSketchData.objectData.width / 2;
        return this.newCircle(entityFromSketchData.objectData);
      case 'doubleArrow':
        return this.newDoubleArrow(entityFromSketchData.objectData);
      case 'arrow':
        return this.newArrow(entityFromSketchData.objectData);
      case 'line':
        return this.newLine(entityFromSketchData.objectData);
      case 'variable':
        return this.newVariable(entityFromSketchData.objectData);
    }
    return null;
  }

  setKonvaWidth(width:number){
    this.konvaWidth = Math.ceil(width);
    this.zoom(this.zoomScale);
  }

  setKonvaHeight(height:number){
    this.konvaHeight = Math.ceil(height);
    this.zoom(this.zoomScale);
  }

  factorZoom(zoomScale: number){

    if(this.previewWidth && this.previewHeight){
      const factor = Math.min(this.previewWidth/this.konvaWidth,this.previewHeight/this.konvaHeight);
      const _zoomScale = Math.round((Math.min(factor,20))*1000)/1000;
      return !isNaN(_zoomScale) ? _zoomScale : zoomScale;
    }
    return zoomScale;

  }

  zoom(zoomScale: number){

    const _zoomScale = this.factorZoom(zoomScale);
    if(this.konva && !isNaN(_zoomScale)){
      this.zoomScale = _zoomScale;
      this.konva.width(this.konvaWidth * _zoomScale);
      this.konva.height( this.konvaHeight * _zoomScale);
      this.konva.scale({x: (this.zoomScale), y: (this.zoomScale)});
      this.handleStrokeWidth(this.zoomScale);
      this.handleFontSize(this.zoomScale);
      this.handleCrossLines(this.zoomScale);
      this.setMarginsPx();

    }

  }

  newRectangle(objectData:any) {
    return new Konva.Rect({
      x:objectData.x,
      y:objectData.y,
      width: objectData.width,
      height: objectData.height,
      fill:objectData.fill,
      draggable: false,
      name: 'entity',
      strokeScaleEnabled: false
    });
  }

  newCircle(objectData:any) {
    return new Konva.Circle({
      x: objectData.x,
      y: objectData.y,
      radius: objectData.radius,
      fill:objectData.fill,
      strokeScaleEnabled: false,
      draggable: false,
      name: 'entity',
    });
  }

  newDoubleArrow(objectData:any){
    return new Konva.Arrow({
      x: objectData.x,
      y: objectData.y,
      points: objectData.points,
      pointerLength: 20,
      pointerWidth: 20,
      fill: objectData.stroke,
      stroke: objectData.stroke,
      strokeWidth: STROKE_WIDTH,
      draggable: false,
      pointerAtBeginning: true,
      pointerAtEnding:true,
      name: 'entity',
    });

  }

  newArrow(objectData:any){
    return new Konva.Arrow({
      x: objectData.x,
      y: objectData.y,
      points: objectData.points,
      pointerLength: 20,
      pointerWidth: 20,
      fill: objectData.stroke,
      stroke: objectData.stroke,
      strokeWidth: STROKE_WIDTH,
      draggable: false,
      name: 'entity',
    });
  }

  newLine(objectData:any) {
    return new Konva.Line({
      x: objectData.x,
      y: objectData.y,
      points: objectData.points,
      stroke: objectData.stroke,
      strokeWidth: STROKE_WIDTH,
      entities: [],
      closed: true,
      draggable: false,
      name: 'entity',
    });
  }

  newVariable(objectData:any){

    return new Konva.Text({
      text: '0',
      fontSize: FONT_SIZE,
      fill: TEXT_COLOR,
      draggable: true,
      name: 'entity',
      x: objectData.x,
      y: objectData.y,
      width: objectData?.width ? objectData.width : 400,
      height: objectData?.height ? objectData.height : 60,
      align: objectData?.align ? objectData.align : 'left',
      verticalAlign: objectData?.verticalAlign ? objectData.verticalAlign : 'bottom'
    });
  }

  async setGraphicFill(entity:any){
    if(
      this._sketchPhotos
      && (entity.graphicalParameters.photo || entity.graphicalParameters.crossLine)
      ){
      if(entity.graphicalParameters.photo){

        this._platform === 'server'
          ? await setPhoto(entity,this.loadedPhotos,this._sketchPhotos,this._host)
          : setPhoto(entity,this.loadedPhotos,this._sketchPhotos,this._host);

        this.handleStretching(entity);
      }else if(entity.graphicalParameters.crossLine){
        this._platform === 'server'
          ? await setCrossLines(entity,this.loadedCrossLines,this._host)
          : setCrossLines(entity,this.loadedCrossLines,this._host);

        this.handleStretching(entity);
      }else{
        entity.konvaObject.fill(DEFAULT_FILL);
      }
    }
  }

  async handleGraphicalParameters(entitiesFromSketchData:any){
    for (const entity of entitiesFromSketchData) {
      entity.graphicalParameters.optionId = null;
      if(['circle','rectangle'].includes(entity.type)){
        this._platform === 'server'
          ? await this.setGraphicFill(entity)
          : this.setGraphicFill(entity);
        this.setEntityGraphicalParameters(entity);
      }
    }
  }

  setEntityGraphicalParameters(entity:any){

    if(!entity.graphicalParameters.stretching){
      entity.graphicalParameters.stretching = 'no-stretching';
    }

    if(entity.graphicalParameters.repeat){
      setRepeat(entity);
    }

    this.handleStretching(entity);

    setPosition(entity,'x');
    setPosition(entity,'y');
    if(entity.graphicalParameters.offsetX !== 0){
      setOffset(entity,'x');
    }
    if(entity.graphicalParameters.offsetY !== 0){
      setOffset(entity,'y');
    }
  }

  addEntityToTreeList(entity:any) {
    this.entitiesTreeList.unshift(entity);
  }

  getEntitiesFlat(entities=this.entitiesTreeList, entitiesFlat:any=[]) {
    entities.forEach((e:any) => {
      entitiesFlat.push(e);
      entitiesFlat = this.getEntitiesFlat(e.entities, entitiesFlat)
    })
    return entitiesFlat;
  }

  findEntityById(entityId:number){
    const entitiesFlat = this.getEntitiesFlat();
    return entitiesFlat.find((entity:any) => entity.id === entityId);
  }

  handleEntityReference(entity:any) {
    if(entity.xParameters.reference.referenceEntity){
      entity.xParameters.reference.referenceEntity = this.findEntityById(entity.xParameters.reference.referenceEntity);
    }
    if(entity.xParameters.dimensioning.reference.referenceEntity){
      entity.xParameters.dimensioning.reference.referenceEntity = this.findEntityById(entity.xParameters.dimensioning.reference.referenceEntity);
    }
    if(entity.yParameters.reference.referenceEntity){
      entity.yParameters.reference.referenceEntity = this.findEntityById(entity.yParameters.reference.referenceEntity);
    }
    if(entity.yParameters.dimensioning.reference.referenceEntity){
      entity.yParameters.dimensioning.reference.referenceEntity = this.findEntityById(entity.yParameters.dimensioning.reference.referenceEntity);
    }
  }

  handleEntitiesReference(entitiesFromSketchData:any) {
    for (const entity of entitiesFromSketchData) {
      this.handleEntityReference(entity);
    }
  }

  getEntitiesFlatChildrenFirst(entities=this.entitiesTreeList, entitiesFlat:any=[]) {
    entities.forEach((e:any) => {
      entitiesFlat = this.getEntitiesFlatChildrenFirst(e.entities, entitiesFlat)
      entitiesFlat.push(e);
    })
    return entitiesFlat;
  }

  setZIndexForEntities() {
    const konvaObjects = this.getEntitiesFlatChildrenFirst().map((e:any) => e.konvaObject);
    let zIndex = konvaObjects.length;
    konvaObjects.forEach((o:any) => {
      o.zIndex(zIndex);
      zIndex -= 1;
    });
  }

  getFeatureValue(fieldId: string):string|null{
    if(this._details?.fields && this._details.fields[fieldId]){
        const field = this._details.fields[fieldId];
        return field.value ? field.value : field.defaultValue;
    }
    return null;
  }

  getFeatureOption(featureId:string, optionId:number):{value:string,img?:string}|null{
    if(this._details?.fields){
      for (const field of Object.values(this._details.fields) as ComplexConfiguratorField[] ) {
        if (String(field.id) === String(featureId)) {
            return field?.productFeatureOptions[optionId];
        }
      }
    }
    return null;
  }

  setEntityWidth(entity:any, width:number) {
    if (!isNaN(width) && width > 0) {
      if (['line','arrow','doubleArrow'].includes(entity.type)) {
        if(entity.parameters.lineOrientation === 'horizontal'){
          if(entity.type === 'doubleArrow')width = width - (this.strokeWidth*2);
          if(entity.type === 'arrow') width = width - this.strokeWidth;
          const points = [... entity.konvaObject.points()];
          if(points[0] < points[2] ){
            entity.konvaObject.points([
              points[0],
              points[1],
              points[0]+width,
              points[3]
            ]);
          }else{
            entity.konvaObject.points([
              points[2]+width,
              points[1],
              points[2],
              points[3]
            ]);

          }
        }
      }else if (['rectangle','variable'].includes(entity.type)) {
        entity.konvaObject.width(width);
      }else if (['circle'].includes(entity.type)) {
        entity.konvaObject.radius(width/2);
      }
      entity.xParameters.dimensioning.value = width;
    }
  }

  findAllEntitiesWithSameFeature(entity: any, axis: 'x'|'y') {
    return this.getEntitiesFlat().filter((e:any) => e !== entity).filter((e:any) => axis === 'x'
      ? e.xParameters.dimensioning.type === 'feature'
        && e.xParameters.dimensioning.feature.fieldId === entity.xParameters.dimensioning.feature.fieldId
      : e.yParameters.dimensioning.type === 'feature'
        && e.yParameters.dimensioning.feature.fieldId === entity.yParameters.dimensioning.feature.fieldId
    )
  }

  findMainEntityWithSameFeature(entity: any, axis: 'x'|'y') {
    const entities = this.findAllEntitiesWithSameFeature(entity, axis);
    return entities.find((e:any) => {
      const entityFeature = axis === 'x' ? e.xParameters.dimensioning.feature : e.xParameters.dimensioning.feature;
      return entityFeature.mainEntityWithThisFeature && entityFeature.mainEntityWithThisFeature === entityFeature.fieldId
    });
  }

  getEntityX(entity:any, entityWidth=this.getEntityWidth(entity)) {
    const referenceEntity = entity.xParameters.reference?.referenceEntity;
    const referenceKonvaObject = referenceEntity?.konvaObject;
    const entityKonvaObjectX = ['circle'].includes(entity.type)
      ? entity.konvaObject.x() - entity.konvaObject.radius()
      : entity.konvaObject.x();

    if (!referenceKonvaObject || !entity.xParameters?.reference?.position || !entity.xParameters?.anchor?.position){
      return entityKonvaObjectX;
    }

    const referenceEntityType = referenceEntity?.type;
    const referenceKonvaObjectX = ['line','arrow','doubleArrow'].includes(referenceEntityType)
          ? referenceKonvaObject.points()[0]
          : ['circle'].includes(referenceEntityType)
            ? referenceKonvaObject.x() - referenceKonvaObject.radius()
            : referenceKonvaObject.x();

    let referenceXPosition;
    switch (entity.xParameters.reference.position) {
      case 'left':
        referenceXPosition = referenceKonvaObjectX;
        break;
      case 'middle':
        referenceXPosition = referenceKonvaObjectX + this.getEntityWidth(referenceEntity) / 2;
        break;
      case 'right':
        referenceXPosition = referenceKonvaObjectX + this.getEntityWidth(referenceEntity);
        break;
    }

    if (!referenceXPosition) return entityKonvaObjectX;

    let anchorXPosition;
    switch (entity.xParameters.anchor.position) {
      case 'left':
        anchorXPosition = 0;
        break;
      case 'middle':
        anchorXPosition = entityWidth / 2;
        break;
      case 'right':
        anchorXPosition = entityWidth;
        break;
    }

    let x = referenceXPosition - anchorXPosition;
    if (entity.xParameters.reference.offset) {
      x += Number(entity.xParameters.reference.offset);
    }
    if (entity.xParameters.anchor.offset) {
      x += Number(entity.xParameters.anchor.offset);
    }
    return x;
  }

  getEntityY(entity:any, entityHeight=this.getEntityHeight(entity)) {
    const referenceEntity = entity.yParameters.reference?.referenceEntity;
    const referenceKonvaObject = referenceEntity?.konvaObject;
    const entityKonvaObjectY = ['circle'].includes(entity.type)
      ? entity.konvaObject.y() - entity.konvaObject.radius()
      : entity.konvaObject.y();

    if (!referenceKonvaObject || !entity.yParameters?.reference?.position || !entity.yParameters?.anchor?.position) {
      return entityKonvaObjectY;
    }

    let referenceYPosition;
    const referenceEntityType = referenceEntity?.type;
    const referenceKonvaObjectY = ['line','arrow','doubleArrow'].includes(referenceEntityType)
          ? referenceKonvaObject.points()[1]
          : ['circle'].includes(referenceEntityType) ?
            referenceKonvaObject.y() - referenceKonvaObject.radius()
            : referenceKonvaObject.y();

    switch (entity.yParameters.reference.position) {
      case 'top':
        referenceYPosition = referenceKonvaObjectY;
        break;
      case 'middle':
        referenceYPosition = referenceKonvaObjectY + this.getEntityHeight(referenceEntity) / 2;
        break;
      case 'bottom':
        referenceYPosition = referenceKonvaObjectY + this.getEntityHeight(referenceEntity);
        break;
    }

    if (!referenceYPosition) return entityKonvaObjectY;

    let anchorYPosition;
    switch (entity.yParameters.anchor.position) {
      case 'top':
        anchorYPosition = 0;
        break;
      case 'middle':
        anchorYPosition = entityHeight / 2;
        break;
      case 'bottom':
        anchorYPosition = entityHeight;
        break;
    }

    let y = referenceYPosition - anchorYPosition;
    if (entity.yParameters.reference.offset) {
      y += Number(entity.yParameters.reference.offset);
    }
    if (entity.yParameters.anchor.offset) {
      y += Number(entity.yParameters.anchor.offset);
    }
    entity.konvaObject.draggable(false);
    return y;
  }

  getEntityWidth(entity:any){
    if(['line','arrow','doubleArrow'].includes(entity.type)){
      if(entity.parameters.lineOrientation === 'horizontal'){
        const points = entity.konvaObject.points();
        return points[2] - points[0];
      }
    }else if(['rectangle','variable'].includes(entity.type)){
      return entity.konvaObject.width();
    }else if(['circle'].includes(entity.type)){
      return entity.konvaObject.radius()*2;
    }
    return 0;
  }

  getEntityHeight(entity:any){
    if(['line','arrow','doubleArrow'].includes(entity.type)){
      if(entity.parameters.lineOrientation === 'vertical'){
        const points = entity.konvaObject.points();
        return points[3] - points[1];
      }
    }else if(['rectangle','variable'].includes(entity.type)){
      return entity.konvaObject.height();
    }else if(['circle'].includes(entity.type)){
      return entity.konvaObject.radius()*2;
    }
    return 0;
  }

  getEntityDimension(entity: any, axis:'x'|'y' ='x') {

    if(!entity){
      return null;
    }

    const dimension = {
      x: this.getEntityX(entity),
      width: this.getEntityWidth(entity),
      y: this.getEntityY(entity),
      height: this.getEntityHeight(entity)
    };

    const dimensioning = axis === 'x' ? entity.xParameters.dimensioning : entity.yParameters.dimensioning;
    const entityWithSameFeature = this.findMainEntityWithSameFeature(entity, axis);
    const referenceEntity = dimensioning.reference.referenceEntity;
    switch(dimensioning?.type) {
      case 'value':
        if (axis === 'x') {
          dimension[axis] = this.getEntityX(entity, dimension.width);
          dimension.width = Number(dimensioning.value);
        } else {
          dimension[axis] = this.getEntityY(entity, dimension.height);
          dimension.height = Number(dimensioning.value);
        }
        break;
      case 'feature':
        if (entityWithSameFeature) {
          if (axis === 'x') {
            dimension.width = entityWithSameFeature.konvaObject.width();
          } else {
            dimension.height = entityWithSameFeature.konvaObject.height();
          }
        }
        dimension[axis] = axis === 'x' ? this.getEntityX(entity, dimension.width) : this.getEntityY(entity, dimension.height);
        break;
      case 'reference':
        if (referenceEntity?.konvaObject) {

          const referencePoint = axis === 'x'
            ? referenceEntity.konvaObject.x() + this.getKonvaObjectXByPosition(referenceEntity.konvaObject, dimensioning.reference.position)
            : referenceEntity.konvaObject.y() + this.getKonvaObjectYByPosition(referenceEntity.konvaObject, dimensioning.reference.position)

          const entityPoint = axis === 'x' ? this.getEntityX(entity) : this.getEntityY(entity);
          let difference = 0;
          let offset = 0;
          if (!isNaN(Number(dimensioning.reference.offset))) {
            offset = Number(dimensioning.reference.offset)
          }

          switch (axis === 'x' ? entity.xParameters.anchor.position : entity.yParameters.anchor.position) {
            case 'left':
            case 'top':

              difference = referencePoint - entityPoint;
              dimension[axis] = entityPoint;
              if (axis === 'x') {

                if( entity.type === 'variable' && entity.konvaObject.rotation() >= 45){
                  dimension.width = this.getEntityHeight(referenceEntity) + offset;
                }else{
                  dimension.width = (['line','arrow','doubleArrow'].includes(referenceEntity.type)
                    ? difference
                    : this.getEntityWidth(referenceEntity)) + offset;
                }

              } else {
                dimension.height = ['line','arrow','doubleArrow'].includes(referenceEntity.type)
                  ? difference + offset
                  : this.getKonvaObjectHeight(referenceEntity.konvaObject);
              }
              break;
            case 'middle':
              difference = referencePoint - (axis === 'x' ? dimension.x + dimension.width / 2 : dimension.y + dimension.height / 2);
              if (!isNaN(Number(dimensioning.reference.offset))) {
                offset = Number(dimensioning.reference.offset)
              }

              if (axis === 'x') {
                dimension[axis] -= referencePoint - (entityPoint + dimension.width) - offset;
                dimension.width += referencePoint - (dimension.x + dimension.width)  + offset;
              } else {
                dimension[axis] -= referencePoint - (entityPoint + dimension.height) - offset;
                dimension.height += referencePoint - (dimension.y + dimension.height) + offset;
              }
              break;
          }
        }
        break;
    }

    return dimension;
  }

  findEntityWithFeature(fieldId:number, axis:'x'|'y') {
    return this.getEntitiesFlat().find((e:any) => {
      const entityFeature = axis === 'x' ? e.xParameters.dimensioning.feature : e.yParameters.dimensioning.feature;
      return entityFeature.fieldId === fieldId;
    })
  }


  setEntityXDefaultValues(entity:any) {
    if (entity.xParameters.reference.position && !entity.xParameters.anchor.position) entity.xParameters.anchor.position = 'left';
    if (entity.xParameters.dimensioning.reference.referenceEntity && !entity.xParameters.dimensioning.reference.position) entity.xParameters.dimensioning.reference.position = 'right';
    if (entity.xParameters.anchor.position === 'right') entity.xParameters.dimensioning.type = 'value';
  }

  setEntityYDefaultValues(entity:any) {
    if (entity.yParameters.reference.position && !entity.yParameters.anchor.position) entity.yParameters.anchor.position = 'top';
    if (entity.yParameters.dimensioning.reference.referenceEntity && !entity.yParameters.dimensioning.reference.position) entity.yParameters.dimensioning.reference.position = 'bottom';
    if (entity.yParameters.anchor.position === 'bottom') entity.yParameters.dimensioning.type = 'value';
  }

  setEntityXDimension(entity:any) {
    this.setEntityXDefaultValues(entity);
    const dimension = this.getEntityDimension(entity, 'x');
    if(dimension){
      this.setEntityWidth(entity, dimension.width);
      this.setEntityX(entity, dimension.x);
    }
  }

  setEntityYDimension(entity:any) {
    this.setEntityYDefaultValues(entity);
    const dimension = this.getEntityDimension(entity, 'y');
    if(dimension){
      if(!['circle'].includes(entity.type)){
        this.setEntityHeight(entity, dimension.height);
      }
      this.setEntityY(entity, dimension.y);
    }
  }

  setEntityX(entity:any, x:number) {
    const referenceEntity = entity.xParameters.reference?.referenceEntity;
    if (referenceEntity) entity.konvaObject.draggable(false);
    entity.konvaObject.x(
      ['circle'].includes(entity.type)
        ?x+(this.getEntityWidth(entity)/2)
        :x
    );
  }

  setEntityY(entity:any, y:number) {
    const referenceEntity = entity.yParameters.reference?.referenceEntity;
    if (referenceEntity) entity.konvaObject.draggable(false);
    entity.konvaObject.y(
      ['circle'].includes(entity.type)
        ?y+(this.getEntityWidth(entity)/2)
        :y
    );
  }

  setEntityHeight(entity:any, height:number) {
    if (!isNaN(height) && height > 0) {
      if (['line','arrow','doubleArrow'].includes(entity.type)) {
        if(entity.parameters.lineOrientation === 'vertical'){
          if(entity.type === 'doubleArrow')height = height - (this.strokeWidth*2);
          if(entity.type === 'arrow') height = height - this.strokeWidth;
          const points = [... entity.konvaObject.points()];
          if(points[1] < points[3]){
            entity.konvaObject.points([
              points[0],
              points[1],
              points[2],
              points[1]+height
            ]);
          }else{
            entity.konvaObject.points([
              points[0],
              points[3]+height,
              points[2],
              points[3]
            ]);

          }
        }
      }else if (['rectangle','variable'].includes(entity.type)) {
        entity.konvaObject.height(height);
      }
      entity.yParameters.dimensioning.value = height;
    }
  }

  getKonvaObjectXByPosition(konvaObject:any, position:string) {
    switch (position) {
      case 'left':
        return 0;
      case 'middle':
        return this.getKonvaObjectWidth(konvaObject) / 2;
      case 'right':
        return this.getKonvaObjectWidth(konvaObject);
    }
  }

  getKonvaObjectYByPosition(konvaObject:any, position:string) {
    switch (position) {
      case 'top':
        return 0;
      case 'middle':
        return this.getKonvaObjectHeight(konvaObject) / 2;
      case 'bottom':
        return this.getKonvaObjectHeight(konvaObject);
    }
  }

  getKonvaObjectWidth(konvaObject:any) {
    return konvaObject.width() * konvaObject.scaleX();
  }
  getKonvaObjectHeight(konvaObject:any) {
    return konvaObject.height() * konvaObject.scaleY();
  }

  handleViewPortMargins(){
    if(this.viewport.entity){

      const marginTop =
        Math.round(
          ((this.getEntityHeight(this.viewport.entity) / 100)* Number(this.viewport.percentages.marginTop))
          + Number(this.viewport.millimeters.marginTop)
        );

      this.konvaHeight += Number(this.marginTopPx);
      if(marginTop)this.konvaHeight +=  Number(marginTop);

      const marginRight =
        Math.round(
          ((this.getEntityWidth(this.viewport.entity) / 100)* Number(this.viewport.percentages.marginRight))
          + Number(this.viewport.millimeters.marginRight)
        );

      this.konvaWidth += Number(this.marginRightPx);
      if(marginRight)this.konvaWidth +=  Number(marginRight);

      const marginBottom =
        Math.round(
          ((this.getEntityHeight(this.viewport.entity) / 100)* Number(this.viewport.percentages.marginBottom))
          + Number(this.viewport.millimeters.marginBottom)
        );

      this.konvaHeight += + Number(this.marginBottomPx);
      if(marginBottom)this.konvaHeight +=  Number(marginBottom);

      const marginLeft =
        Math.round(
          ((this.getEntityWidth(this.viewport.entity) / 100)* Number(this.viewport.percentages.marginLeft))
          + Number(this.viewport.millimeters.marginLeft)
        );

      this.konvaWidth +=  Number(this.marginLeftPx);
      if(marginLeft)this.konvaWidth +=  Number(marginLeft);

    }
  }

  getGlobalWidth():number{
    if(this.el?.nativeElement?.offsetWidth){
      return + (this.el.nativeElement.offsetWidth + 100)
    }else if (this.konvaWidth){
      return this.konvaWidth;
    }else{
      return 100;
    }
  }

  getGlobalHeight():number{
    if(this.el?.nativeElement?.offsetHeight){
      return + (this.el.nativeElement.offsetHeight + 100)
    }else if (this.konvaHeight){
      return this.konvaHeight;
    }else{
      return 100;
    }
  }




  handleViewPort(){
    if(this.viewport.entity){
      this.konvaWidth = this.getEntityWidth(this.viewport.entity);
      this.konvaHeight = this.getEntityHeight(this.viewport.entity);
      this.handleViewPortMargins();
      this.setKonvaWidth(this.konvaWidth);
      this.setKonvaHeight(this.konvaHeight);
    }
  }

  calculateStrokeWidth(zoomScale:number){
    const stokeWidth =  Math.round(STROKE_WIDTH/zoomScale);
    return stokeWidth < MIN_STROKE_WIDTH ? MIN_STROKE_WIDTH : Math.min(stokeWidth,MAX_STROKE_WIDTH);
  }

  calculateFontSize(zoomScale:number){
    const fontSize =  Math.round(FONT_SIZE/zoomScale);
    return fontSize < MIN_FONT_SIZE ? MIN_FONT_SIZE : Math.min(fontSize,MAX_FONT_SIZE);
  }

  handleStrokeWidth(zoomScale:number){
    this.strokeWidth = this.calculateStrokeWidth(zoomScale);
    const entitiesFlat = this.getEntitiesFlat();
    if(this.strokeWidth){
      for (const entity of entitiesFlat) {
        if(['line','arrow','doubleArrow'].includes(entity.type)){
          entity.konvaObject.strokeWidth(this.strokeWidth);
        }
      }
    }
  }

  handleFontSize(zoomScale:number){
    const fontSize = this.calculateFontSize(zoomScale);
    const entitiesFlat = this.getEntitiesFlat();
    if(fontSize){
      for (const entity of entitiesFlat) {
        if(['variable'].includes(entity.type)){
          entity.konvaObject.fontSize(fontSize);
        }
      }
    }
  }

  calculatePercentWidthAndHeight(zoomScale:number){
    return Math.round(25/zoomScale);
  }


  handleCrossLines(zoomScale:number){
    const percentWidthAndHeight = this.calculatePercentWidthAndHeight(zoomScale);
    const entitiesFlat = this.getEntitiesFlat();
    for (const entity of entitiesFlat) {
        if(entity.graphicalParameters.crossLine){
          entity.graphicalParameters.percentWidth = percentWidthAndHeight;
          entity.graphicalParameters.percentHeight = percentWidthAndHeight;
          setScale(entity,'width');
          setScale(entity,'height');
        }
    }
  }

  isHex(value: string):boolean{
    return /^#[0-9A-F]{6}$/i.test(value);
  }

  getOptionPhotos(defaultOnly = false):Record<string,string|number>[]{
    let optionPhotos:Record<string,string|number>[] = [];

    if(!this._optionPhotos && this._details?.fields){
      for (const field of Object.values(this._details.fields) as ComplexConfiguratorField[]) {
        if(field?.type === 'select' && field?.productFeatureOptionsIds?.length && field?.productFeatureOptions) {
          const defaultValue = this.getFeatureValue(field.id);
          for (const optionId of field?.productFeatureOptionsIds) {
            const option = field?.productFeatureOptions[optionId];
            if(option?.img && (!defaultOnly || + defaultValue === optionId )){
              optionPhotos.push(
                {
                  img:option.img,
                  width_mm:option.widthMM,
                  height_mm:option.heightMM,
                }
                );
            }
          }
        }
      }
      if(!defaultOnly){
        this._optionPhotos = optionPhotos;
      }
    }else{
      optionPhotos = this._optionPhotos;
    }

    return optionPhotos;

  }

  handleEmitSketchData(){
    if(this.getSketchData && this.konva){
      const workspace = this.prepareWorkspaceData();
      const entities = this.prepareEntitiesData(this.entitiesTreeList);
      const photos = this._sketchPhotos;
      const optionPhotos = this._optionPhotos;
      const details = this._details;
      this.sketchDataEmitter.emit({
        workspace,
        entities,
        photos,
        optionPhotos,
      });
      this.getSketchData = false;
    }
  }

  prepareObjectData(entity:any){
    let width = this.getEntityWidth(entity);
    let height = this.getEntityHeight(entity);
    if(width<0)width = -width;
    if(height<0)height = -height;

    const radius = typeof entity.konvaObject.radius !== 'undefined' ? entity.konvaObject.radius() : null;
    const points = typeof entity.konvaObject.points !== 'undefined' ? entity.konvaObject.points() : null;
    const stroke = typeof entity.konvaObject.stroke !== 'undefined' ? entity.konvaObject.stroke() : null;
    const fill = typeof entity.konvaObject.fill !== 'undefined' ? entity.konvaObject.fill() : null;
    const rotation = typeof entity.konvaObject.rotation !== 'undefined' ? entity.konvaObject.rotation() : null;
    const zIndex = typeof entity.konvaObject.zIndex !== 'undefined' ? entity.konvaObject.zIndex() : null;
    const align = typeof entity.konvaObject.align !== 'undefined' ? entity.konvaObject.align() : null;
    const verticalAlign = typeof entity.konvaObject.verticalAlign !== 'undefined' ? entity.konvaObject.verticalAlign() : null;

    return {
      x: this.getEntityX(entity),
      y: this.getEntityY(entity),
      width,
      height,
      radius,
      points,
      stroke,
      fill,
      rotation,
      zIndex,
      align,
      verticalAlign
    }
  }

  prepareEntity(entity:any):any{
    return {
      id: entity.id,
      type: entity.type,
      transformingDisabled: entity.transformingDisabled,
      name: entity.name,
      iconClass: entity.iconClass,
      parameters: entity.parameters,
      graphicalParameters: entity.graphicalParameters,
      xParameters: entity.xParameters,
      yParameters: entity.yParameters,
      mirror: entity.mirror,
      objectData: this.prepareObjectData(entity),
      childrenEntities:[]
    }
  }

  prepareEntityReference(entity:any){
    if(entity.xParameters.reference.referenceEntity){
      entity.xParameters.reference.referenceEntity = entity.xParameters.reference.referenceEntity.id;
    }
    if(entity.xParameters.dimensioning.reference.referenceEntity){
      entity.xParameters.dimensioning.reference.referenceEntity = entity.xParameters.dimensioning.reference.referenceEntity.id;
    }
    if(entity.yParameters.reference.referenceEntity){
      entity.yParameters.reference.referenceEntity = entity.yParameters.reference.referenceEntity.id;
    }
    if(entity.yParameters.dimensioning.reference.referenceEntity){
      entity.yParameters.dimensioning.reference.referenceEntity = entity.yParameters.dimensioning.reference.referenceEntity.id;
    }
  }

  prepareEntitiesData(entities:any):any[]{
    const entitiesData = [];
    for (const entity of entities) {
      this.prepareEntityReference(entity);
      entitiesData.push(this.prepareEntity(entity));
    }
    return entitiesData;
  }

  prepareViewportEntity(){
    if(this.viewport.entity){
      this.viewport.entity = this.viewport.entity.id;
    }
    return this.viewport;
  }

  prepareWorkspaceData(){
    return {
      width:this.konvaWidth,
      height:this.konvaHeight,
      zoomScale:this.zoomScale,
      viewport: this.prepareViewportEntity()
    };
  }

  setMarginsPx(){
    if(this.viewport?.pixels?.marginTop && this.zoomScale){
      this.marginTopPx = this.viewport?.pixels?.marginTop  / Math.min(this.zoomScale,1);
    }
    if(this.viewport?.pixels?.marginRight && this.zoomScale){
      this.marginRightPx = this.viewport?.pixels?.marginRight / Math.min(this.zoomScale,1);
    }
    if(this.viewport?.pixels?.marginBottom && this.zoomScale){
      this.marginBottomPx = this.viewport?.pixels?.marginBottom  / Math.min(this.zoomScale,1);
    }
    if(this.viewport?.pixels?.marginLeft && this.zoomScale){
      this.marginLeftPx = this.viewport?.pixels?.marginLeft  / Math.min(this.zoomScale,1);
    }
  }

}
